import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RestService } from '../../../services/rest/rest.service';
import { AccountsSortingService } from '../../../features/accounts/services/accounts-sorting.service';
import { AccountsDescriptionService } from '../../../features/accounts/services/accounts-description.service';
import { RestJsonCodecService } from '../../../services/rest/rest-json-codec.service';
import { RecurringCashTransferStatus } from '../cash/cash-transfer.domain';
import {
  OfiAccountsResponse,
  OfiRecurringExternalAccounts,
  OfiRecurringTransferAgreement,
  OfiRecurringTransferDisclosure,
  OfiTransfer,
  OfiTransferRequest,
  OfiTransferResponse
} from './ofi-transfers.domain';

@Injectable()
export class OfiTransfersRestService {
  private readonly codec = inject(RestJsonCodecService);
  private readonly restService = inject(RestService);
  private readonly accountsDescriptionService = inject(AccountsDescriptionService);
  private readonly accountsSortingService = inject(AccountsSortingService);

  getInteracAccounts(): Observable<OfiAccountsResponse> {
    return this.restService
      .getResource('ofi_transfer.accounts')
      .get<OfiAccountsResponse>()
      .pipe(map((response: OfiAccountsResponse): OfiAccountsResponse => {
        this.accountsDescriptionService.setDescriptions(response.fromAccounts);
        this.accountsSortingService.sort(response.fromAccounts);
        return response;
      }));
  }

  getRecurringAccounts(): Observable<OfiAccountsResponse> {
    return this.restService
      .getResource('ofi_recurring_transfer.accounts')
      .get<OfiAccountsResponse>()
      .pipe(map((response: OfiAccountsResponse): OfiAccountsResponse => {
        this.accountsDescriptionService.setDescriptions(response.fromAccounts);
        this.accountsSortingService.sort(response.fromAccounts);
        return response;
      }));
  }

  postInteracTransfer(ofiTransferRequest: OfiTransferRequest): Observable<OfiTransferResponse> {
    return this.restService
      .getResource('ofi_transfer.interacTransfer')
      .post(ofiTransferRequest);
  }

  postRecurringTransfer(ofiRecurringData: OfiTransfer): Observable<string> {
    return this.restService.getResource('ofi_recurring_transfer.submit-creation-requests').post(this.getFormData(ofiRecurringData));
  }

  postRecurringTransferLite(ofiRecurringData: OfiTransfer): Observable<string> {
    return this.restService
      .getResource('ofi_recurring_transfer.submit-creation-requests-lite')
      .post(this.getFormDataLite(ofiRecurringData));
  }

  getRecurringExternalAccounts(csSessionId: string): Observable<OfiRecurringExternalAccounts> {
    const parameters = { csSessionId };
    return this.restService
      .getResource('ofi_recurring_transfer.ofi-accounts')
      .post<typeof parameters, OfiRecurringExternalAccounts>(parameters);
  }

  getRecurringTransferStatus(): Observable<Array<RecurringCashTransferStatus>> {
    // Backend requirement to pass these 2 params for ofi ticket status call.
    const withValidatedFromAccount = false;
    const onlyOFIRecurring = true;

    return this.restService
      .getResource('recurringTransfer.ticketStatus')
      .get<Array<RecurringCashTransferStatus>>({
        params: { withValidatedFromAccount, onlyOFIRecurring }
      })
      .pipe(
        map((cashTransferStatuses: Array<RecurringCashTransferStatus>): Array<RecurringCashTransferStatus> => {
          cashTransferStatuses.forEach((transferStatus: RecurringCashTransferStatus): void => {
            this.accountsDescriptionService.setAccountDescription(transferStatus.toAccount);
          });
          return cashTransferStatuses;
        }));
  }

  getTransferStatusSummary(): Observable<Array<RecurringCashTransferStatus>> {
    return this.restService
      .getResource('recurringTransfer.ticketStatusSummary')
      .get<Array<RecurringCashTransferStatus>>();
  }

  getOfiDisclosure(): Observable<OfiRecurringTransferDisclosure> {
    return this.restService
      .getResource('ofi_recurring_transfer.disclosure')
      .get<OfiRecurringTransferDisclosure>();
  }

  postOfiAgreement(): Observable<OfiRecurringTransferAgreement> {
    return this.restService
      .getResource('ofi_recurring_transfer.capture-agreement-and-fetch-link')
      .post(undefined);
  }

  private getFormData(transferObject: OfiTransfer): FormData {
    const formData: FormData = new FormData();
    formData.append('voidCheque', transferObject?.voidCheque[0]?.file);
    const contributionType = transferObject.contributionType;
    const parameters = {
      selectedToAccountSessionKey: transferObject.selectedToAccount.sessionKey,
      selectedToAccount: transferObject.selectedToAccount.id,
      transferAmount: transferObject.transferAmount,
      frequencyType: transferObject.frequencyType,
      ...contributionType && { contributionType },
      externalInstitutionNumber: transferObject.externalInstitutionNumber,
      externalTransitNumber: transferObject.externalTransitNumber,
      externalAccountNumber: transferObject.externalAccountNumber,
      startDate: transferObject.startDate
    };
    formData.append('submitRecurringOFIRequest', new Blob([this.codec.encode(parameters)], { type: 'application/json' }));
    return formData;
  }

  private getFormDataLite(transferObject: OfiTransfer): OfiTransferRequest {
    const parameters: OfiTransferRequest = {
      selectedToAccountSessionKey: transferObject.selectedToAccount.sessionKey,
      selectedToAccount: transferObject.selectedToAccount.id,
      transferAmount: transferObject.transferAmount,
      frequencyType: transferObject.frequencyType,
      contributionType: transferObject?.contributionType,
      externalInstitutionNumber: transferObject.externalInstitutionNumber,
      externalTransitNumber: transferObject.externalTransitNumber,
      externalAccountNumber: transferObject.externalAccountNumber,
      startDate: transferObject.startDate,
      csSessionId: transferObject.csSessionId
    };

    return parameters;
  }
}

